import React, { useState, useEffect, useRef } from 'react';
import { expedientesAPI } from '../../services/api';

/** DNI → nombre (RENIEC o BD) → celular opcional */
const ClienteDniFirst = ({
  clienteId,
  onClienteIdChange,
  onDraftChange,
}) => {
  const [dni, setDni] = useState('');
  const [nombre, setNombre] = useState('');
  const [celular, setCelular] = useState('');
  const [searchingDni, setSearchingDni] = useState(false);
  const [hint, setHint] = useState(null);
  const lastLookupDni = useRef('');

  useEffect(() => {
    onDraftChange?.({ dni, nombre, celular });
  }, [dni, nombre, celular, onDraftChange]);

  const applyDatabaseHit = (data) => {
    setNombre(data.nombreCompleto || '');
    setCelular(data.celular || '');
    onClienteIdChange(String(data.id));
    setHint({
      type: 'existing',
      text: data.expedientesCount > 0
        ? `Cliente registrado · ${data.expedientesCount} expediente${data.expedientesCount === 1 ? '' : 's'}`
        : 'Cliente registrado',
    });
  };

  const lookupDni = async (dniValue) => {
    if (dniValue.length !== 8 || !/^\d{8}$/.test(dniValue)) return;
    if (lastLookupDni.current === dniValue) return;

    lastLookupDni.current = dniValue;
    setSearchingDni(true);
    setHint(null);
    onClienteIdChange('');

    try {
      const response = await expedientesAPI.buscarDNI(dniValue);
      if (response.data?.success && response.data.source === 'database') {
        applyDatabaseHit(response.data.data);
        return;
      }
      if (response.data?.success) {
        const data = response.data.data;
        const nombreCompleto = data.nombreCompleto
          || `${data.nombres || ''} ${data.apellidoPaterno || ''} ${data.apellidoMaterno || ''}`.trim();
        setNombre(nombreCompleto);
        setHint({ type: 'reniec', text: 'Datos desde RENIEC — cliente nuevo' });
        return;
      }
      setHint({ type: 'manual', text: 'Ingrese el nombre manualmente' });
    } catch (err) {
      if (err.response?.status === 404) {
        setHint({ type: 'manual', text: 'DNI no encontrado — ingrese el nombre manualmente' });
      } else {
        setHint({ type: 'manual', text: 'No se pudo consultar el DNI — ingrese el nombre manualmente' });
      }
    } finally {
      setSearchingDni(false);
    }
  };

  const handleDniChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 8);
    setDni(val);
    if (val.length < 8) {
      lastLookupDni.current = '';
      setHint(null);
      onClienteIdChange('');
      if (val.length === 0) setNombre('');
    }
  };

  const handleDniBlur = () => {
    if (dni.length === 8) lookupDni(dni);
  };

  useEffect(() => {
    if (dni.length === 8 && /^\d{8}$/.test(dni)) {
      lookupDni(dni);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dni]);

  return (
    <div className="cliente-dni-first">
      <div className="expediente-form-field">
        <label htmlFor="clienteDni">
          DNI *
          {searchingDni && <span className="cliente-dni-first-loading"> · consultando…</span>}
        </label>
        <input
          id="clienteDni"
          inputMode="numeric"
          autoComplete="off"
          value={dni}
          onChange={handleDniChange}
          onBlur={handleDniBlur}
          placeholder="8 dígitos"
          maxLength={8}
          required
        />
      </div>

      {hint && (
        <p className={`cliente-dni-first-hint is-${hint.type}`}>{hint.text}</p>
      )}

      <div className="expediente-form-field">
        <label htmlFor="clienteNombre">Nombre *</label>
        <input
          id="clienteNombre"
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            if (clienteId && hint?.type !== 'existing') onClienteIdChange('');
          }}
          readOnly={hint?.type === 'existing'}
          required
          placeholder="Nombre completo"
        />
      </div>

      <div className="expediente-form-field">
        <label htmlFor="clienteCelular">Celular</label>
        <input
          id="clienteCelular"
          inputMode="numeric"
          value={celular}
          onChange={(e) => setCelular(e.target.value.replace(/\D/g, '').slice(0, 9))}
          placeholder="Opcional · 9 dígitos"
          maxLength={9}
        />
      </div>
    </div>
  );
};

export default ClienteDniFirst;
